/** component logic:
 * - when mounted, set loading to true
 * - check if URL has `code` query param
 * - if yes, POST the `code` to the slack API endpoint to finish integration
 * - dispatch to /integrations
 * - based on summaries.length, show/hide Add to Slack btn / Connected to Slack btn
 */
import { get } from 'lodash';
import React from 'react';
import { Grid } from '@material-ui/core';
import { SettingsPageIntegrationsStoreWrapper } from './store';
import { PauseTable, PauseTableContainer } from '../../components/PauseTable';
import { PauseTableTitleActions } from '../../components/PauseTable/components/PauseTableTitleActions';
import { Plus } from '../../../assets/icons/feather/components';
import { PauseTableHead, PauseTableHeadCell } from '../../components/PauseTable/components/PauseTableHead';
import {
  PauseTableBody,
  PauseTableBodyCell,
  PauseTableRow,
} from '../../components/PauseTable/components/PauseTableRow';
import { PauseTypography } from '../../components/PauseTypography';
import { getFriendlyTime } from '../../../utils/datetime';
import { PauseLoader } from '../../components/PauseLoader';
import { EditSlackIntegrationJourney } from './components/EditSlackIntegrationJourney';
import {
  SLACK_INTEGRATION_JOURNEY_EDIT_KEY,
  SLACK_INTEGRATION_JOURNEY_KEY, SLACK_PUBLIC_HOLIDAY_SETTINGS_JOURNEY_KEY,
  SLACK_PUBLIC_HOLIDAY_SETTINGS_JOURNEY_EDIT_KEY,
  useEnhancedRouter,
} from '../../../router';
import { PauseButton } from '../../components/PauseButton';
import SlackMark from '../../../assets/icons/svg/SlackMark';
import { SpecialSettings } from './components/SpecialSettings';
import { PauseTableTitleWithToggle } from '../../components/PauseTable/components/PauseTableTitleWithToggle';
import { PublicHolidaySlackNotificationSettingsModal } from './components/PublicHolidaySlackNotificationSettingsModal';
import { Journey } from '../_common/Journey';
import { SlackPHDaysInAdvanceOptions } from '../../api/settings';
import {
  PauseModal, PauseModalBody, PauseModalFooter, PauseModalTitle,
} from '../../components/PauseModal';
import { PauseInteractiveButton } from '../../components/PauseInteractiveButton';
import { HowtoLink } from '../../components/HowtoLink';

const summaryTypes = [
  {
    name: 'daily-summaries', type: 'daily', infoText: 'Sends a daily summary of who is on leave to a pre-selected channel.', title: 'Daily summaries',
  },
  {
    name: 'weekly-summaries', type: 'weekly', infoText: 'Sends a weekly summary of who is on leave to a pre-selected channel.', title: 'Weekly summaries',
  },
];

type GetChannelName = (channels: {
  label: string,
  value: string,
  isVisibleToUser: boolean,
}[]) => (id: string) => string
const getChannelName: GetChannelName = (channels = []) => (id) => {
  if (!channels || !channels.length) return '';
  const match = (channels || []).find((c) => c.value === id && c.isVisibleToUser);
  return match ? `#${get((channels || []).find((c) => c.value === id), 'label', '')}` : 'Private channel';
};

const SlackConnectorCard = ({
  isConnected,
  shouldReconnect,
  shouldReconnectForPrivateChannels = false,
  slackURL,
  onDisconnect = () => null,
}: {
  isConnected: boolean,
  shouldReconnect: boolean,
  shouldReconnectForPrivateChannels?: boolean,
  slackURL: string,
  onDisconnect: () => any
}) => (
  <div>
    <div css={(theme) => ({
      display: 'grid',
      gridAutoFlow: 'columns',
      gridTemplateColumns: '3fr minmax(150px, auto)',
      justifyContent: 'space-between',
      alignItems: 'center',
      border: `1px solid ${get(theme, 'palette.pause.background.dark')}`,
      background: get(theme, 'palette.pause.background.medium'),
      padding: theme.spacing(2),
      borderRadius: theme.spacing(1),
    })}
    >
      <div css={(theme) => ({
        display: 'grid',
        gridAutoFlow: 'columns',
        gridTemplateColumns: '64px auto',
        alignItems: 'center',
        gridGap: theme.spacing(1),
      })}
      >
        <div>
          <SlackMark width="4em" height="4em" />
        </div>
        <div>
          <PauseTypography variant="title">
            {isConnected ? 'Slack connected' : 'Connect to Slack'}
          </PauseTypography>
          <PauseTypography color="primary.light">
            {shouldReconnect && (
            <>
              {/* eslint-disable-next-line */}
              <a
                css={(theme) => ({
                  color: get(theme, 'palette.pause.primary.main'),
                  textDecoration: 'underline',
                  cursor: 'pointer',
                })}
                onClick={() => window.location.assign(slackURL)}
              >
                Reconnect
              </a>
              {' '}
              with Slack for more control on summaries.
            </>
            )}
            {!shouldReconnect && !isConnected && 'Connect your organization\'s Slack to the Pause app'}
            {isConnected && !shouldReconnect && shouldReconnectForPrivateChannels && (
            <>
              {/* eslint-disable-next-line */}
              <a
                css={(theme) => ({
                  color: get(theme, 'palette.pause.primary.main'),
                  textDecoration: 'underline',
                  cursor: 'pointer',
                })}
                onClick={() => window.location.assign(slackURL)}
              >
                Reconnect
              </a>
              {' '}
              with Slack to to post leave summary notifications on private channels too.
            </>
            )}
          </PauseTypography>
        </div>
      </div>
      <div>
        {isConnected && (
          <>
            <PauseButton
              variant="outlined"
              color="error"
              onClick={onDisconnect}
            >
              Disconnect
            </PauseButton>
          </>
        )}
        {!isConnected && (
        <>
          <PauseButton
            variant="outlined"
            onClick={() => window.location.assign(slackURL)}
          >
            Connect
          </PauseButton>
        </>
        )}
      </div>
    </div>
  </div>
);

const SlackDisconnectConfirmationModal = ({
  visible = false,
  onClose = () => null,
  onDisconnect = () => null,
}: {
  visible: boolean,
  onClose: () => any,
  onDisconnect: () => any,
}) => (
  <PauseModal
    width="25vw"
    open={visible}
    onClose={onClose}
  >
    <PauseModalTitle title="Confirm disconnection" onClose={onClose} />
    <PauseModalBody>
      <PauseTypography>
        This will remove the Pause Bot from
        your workspace and all notifications will stop.
        All your Slack bot customisations will also be lost. Are you sure you want to disconnect?
      </PauseTypography>
    </PauseModalBody>
    <PauseModalFooter>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <PauseButton onClick={onClose} variant="outlined">Cancel</PauseButton>
        </Grid>
        <Grid item xs={6}>
          <PauseInteractiveButton onClick={() => onDisconnect().then(onClose)} color="error">Disconnect</PauseInteractiveButton>
        </Grid>
      </Grid>
    </PauseModalFooter>
  </PauseModal>
);

const getFrequencyText = ({ notificationDayOfWeek = '' } = {}) => {
  if (notificationDayOfWeek) {
    return `Every ${notificationDayOfWeek[0] + notificationDayOfWeek.slice(1).toLowerCase()}`;
  }
  return 'Every day';
};

export const Integrations = () => {
  const router = useEnhancedRouter();
  const [showSlackDisconnectConfirm,
    setShowSlackDisconnectConfirm] = React.useState<boolean>(false);
  return (
    <SettingsPageIntegrationsStoreWrapper>
      {({
        done,
        integrations,
        slackChannels,
        initiateSummaryIntegrationURL,
        toggleSpecialSettings,
        onSlackDisconnect,
      }) => (
        <div>
          <SlackDisconnectConfirmationModal
            visible={showSlackDisconnectConfirm}
            onClose={() => setShowSlackDisconnectConfirm(false)}
            onDisconnect={onSlackDisconnect}
          />
          <EditSlackIntegrationJourney />
          {!done
            ? <PauseLoader showPageTitle={false} />
            : (
              <div css={(theme) => ({
                display: 'grid',
                gridGap: theme.spacing(5),
              })}
              >
                <SlackConnectorCard
                  isConnected={get(integrations, 'slack.bot.installed')}
                  shouldReconnect={get(integrations, 'slack.bot.installed') && !get(integrations, 'slack.supportsBotSummaries', false)}
                  slackURL={initiateSummaryIntegrationURL}
                  onDisconnect={() => setShowSlackDisconnectConfirm(true)}
                  shouldReconnectForPrivateChannels={get(integrations, 'slack.supportsBotSummaries', false) && !get(integrations, 'slack.supportsPrivateChannels', false)}
                />
                {summaryTypes.map((st) => (
                  <React.Fragment key={st.type}>
                    <PauseTableContainer>
                      <PauseTableTitleWithToggle
                        hideToggle
                        subTitle={(
                          <div>
                            <HowtoLink
                              linkText={`How to add a ${st.type} summary?`}
                              link="https://support.getpause.com/en/article/receive-edit-or-disable-leave-summary-notifications-on-slack-39shdk/"
                            />
                          </div>
)}
                        {...(get(integrations, 'slack.supportsBotSummaries', false) && {
                          actions: (
                            <PauseTableTitleActions
                              actions={[{
                                icon: <Plus />,
                                text: 'Add new',
                                onClick: () => {
                                  // eslint-disable-next-line no-undef
                                  if (st.type === 'daily') {
                                    router.push(
                                      router.getStopHref(
                                        SLACK_INTEGRATION_JOURNEY_KEY,
                                        SLACK_INTEGRATION_JOURNEY_EDIT_KEY,
                                        { type: 'daily' },
                                      ),
                                    );
                                  }
                                  if (st.type === 'weekly') {
                                    router.push(
                                      router.getStopHref(
                                        SLACK_INTEGRATION_JOURNEY_KEY,
                                        SLACK_INTEGRATION_JOURNEY_EDIT_KEY,
                                        { type: 'weekly' },
                                      ),
                                    );
                                  }
                                },
                              }]}
                            />
                          ),
                        })}
                        info={{
                          text: st.infoText,
                        }}
                        title={st.title}
                      />
                      {(get(integrations, 'slack.summaries') || [])
                        .filter((summary) => summary.type === st.type)
                        .length > 0
                            && (
                            <PauseTable>
                              <PauseTableHead>
                                <PauseTableHeadCell>
                                  notification from
                                </PauseTableHeadCell>
                                <PauseTableHeadCell align="right">
                                  slack channel
                                </PauseTableHeadCell>
                              </PauseTableHead>
                              <PauseTableBody>
                                {(get(integrations, 'slack.summaries') || [])
                                  .filter((summary) => summary.type === st.type)
                                  .map((integration) => (
                                    <PauseTableRow
                                      key={integration.id}
                                      onClick={() => {
                                        router.push(router.getStopHref(
                                          SLACK_INTEGRATION_JOURNEY_KEY,
                                          SLACK_INTEGRATION_JOURNEY_EDIT_KEY, {
                                            id: get(integration, 'id'),
                                            ...(!get(integration, 'deprecated') && { type: get(integration, 'type') }),
                                          },
                                        ));
                                      }}
                                    >
                                      <PauseTableBodyCell width="30%">
                                        <PauseTypography
                                          variant="body1"
                                          color="pause.primary.dark"
                                        >
                                          {get(integration, 'teamName')}
                                        </PauseTypography>
                                        <PauseTypography
                                          variant="body2"
                                          color="pause.primary.medium"
                                        >
                                          {
                                            getFrequencyText(integration)
                                          }
                                          {' '}
                                          at
                                          {' '}
                                          {getFriendlyTime(get(integration, 'notificationTime'))}
                                        </PauseTypography>
                                      </PauseTableBodyCell>
                                      <PauseTableBodyCell width="70%" align="right">

                                        <PauseTypography variant="body1" color="pause.primary.medium">
                                          {getChannelName(slackChannels)(get(integration, 'slackChannelID'))}
                                        </PauseTypography>

                                      </PauseTableBodyCell>
                                    </PauseTableRow>
                                  ))}

                              </PauseTableBody>
                            </PauseTable>
                            )}
                    </PauseTableContainer>
                  </React.Fragment>
                ))}
                <SpecialSettings
                  enabled={get(integrations, 'slack.supportsBotSummaries', false)}
                  birthdays={get(integrations, 'slack.birthdays', null)}
                  workAnniversary={get(integrations, 'slack.workAnniversary', null)}
                  publicHolidays={get(integrations, 'slack.publicHolidays', null)}
                  onToggleSetting={toggleSpecialSettings}
                  slackChannels={slackChannels}
                />
                {/* @ts-ignore */}
                <Journey
                  journey={SLACK_PUBLIC_HOLIDAY_SETTINGS_JOURNEY_KEY}
                  stop={SLACK_PUBLIC_HOLIDAY_SETTINGS_JOURNEY_EDIT_KEY}
                >
                  <PublicHolidaySlackNotificationSettingsModal
                    channel={get(integrations, 'slack.publicHolidays.channel', '')}
                    slackChannels={slackChannels}
                    daysInAdvance={get(integrations, 'slack.publicHolidays.daysInAdvance', SlackPHDaysInAdvanceOptions.OneWeek)}
                    time={get(integrations, 'slack.publicHolidays.time', '09:00:00')}
                    dayOfWeek={get(integrations, 'slack.publicHolidays.dayOfWeek', 'MONDAY')}
                    onClose={() => router.endJourney()}
                    onToggleSetting={toggleSpecialSettings}
                  />
                </Journey>
              </div>
            )}
        </div>
      )}
    </SettingsPageIntegrationsStoreWrapper>
  );
};
